/*
 * Copyright (c) Vaisala Oyj. All rights reserved.
 */

import * as React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { setConfiguration } from 'react-grid-system';
import { useTranslation } from 'react-i18next';
import 'reflect-metadata';

import { ApplicationTheme, PageContainer, Application as VaiApplication } from '@vaisala/rockhopper-components';

import LanguageController from '../modules/LanguageController';

import { Fallback } from './Fallback';
import { Header } from './Header';
import { ModalProps, Modals, ModalType } from './custom/Modals';
import { MobileMenu } from './custom/MobileMenu';
import LandingPage from '../pages/LandingPage';
import { OptionsConfig } from './custom/OptionsModal';
import UnitController from '../modules/units/UnitController';
import Analytics from '../modules/analytics/Analytics';
import PersistenceController from '../modules/persistence/PersistenceController';
import PwaController from '../modules/pwa/PwaController';
import { useServiceWorker } from '../components/context/ServiceWorkerContext';

// setup layout grid
setConfiguration({ defaultScreenClass: 'sm', containerWidths: [540, 540, 1100, 1100, 1100], gutterWidth: 16 });

export const ApplicationRoot = () => {
  const { t, i18n } = useTranslation();
  Object.assign(LanguageController, { t, i18n });

  const pageTitle = 'ui.title';

  ////////////////////////////////////////////////////////////
  // MENUS
  ////////////////////////////////////////////////////////////

  const [theme, setTheme] = React.useState(ApplicationTheme.Light);
  const toggleTheme = () => changeTheme(theme === ApplicationTheme.Light ? ApplicationTheme.Dark : ApplicationTheme.Light);
  const chooseTheme = (ind: number) => changeTheme(ind === 0 ? ApplicationTheme.Light : ApplicationTheme.Dark);

  const changeTheme = (t: ApplicationTheme) => {
    PersistenceController.store('theme', t);
    setTheme(t);

    // fix Rockhopper bug (theme css class is not set properly, leaving modals always in light mode)
    document.body.classList[t === ApplicationTheme.Dark ? 'add' : 'remove']('vai-theme-dark');

    Analytics.log('settings.theme', t === ApplicationTheme.Light ? 'light' : 'dark');
  };

  const [unit, setUnit] = React.useState(0);
  const chooseUnit = (ind: number) => {
    UnitController.currentModeIndex = ind;
    PersistenceController.store('unitIndex', ind);
    setUnit(ind);
  };

  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const onMobileMenuOpen = () => {
    setMobileMenuOpen(true);
    Analytics.log('navigation.mobilemenu', 'open');
  };
  const onMobileMenuClose = () => {
    setMobileMenuOpen(false);
    Analytics.log('navigation.mobilemenu', 'close');
  };

  ////////////////////////////////////////////////////////////
  // MODALS
  ////////////////////////////////////////////////////////////

  const [modal, setModal] = React.useState<ModalProps>();
  const modalId = (s: string) => s.split('.')[1];
  const openModal = (type: ModalType, title?: string, optionsConfig?: OptionsConfig, onAccept?: VoidFunction, onReject?: VoidFunction) => {
    onMobileMenuClose();
    setModal({
      currentModal: type,
      onCloseModal: onCloseModal,
      title: title,
      optionsConfig: optionsConfig,
      onAccept: onAccept,
      onReject: onReject,
    });
    Analytics.log('navigation.modal', 'open', modalId(type));
  };

  const onCloseModal = () => {
    Analytics.log('navigation.modal', 'close');
    setModal(undefined);
  };

  const onHelpOpen = () => {
    openModal('modal.help');
  };

  const onOptionsOpen = (title: string, config: OptionsConfig) => {
    openModal('modal.options', title, {
      options: config.options,
      selectedIndex: config.selectedIndex,
      onSelect: (i: number) => {
        config.onSelect(i);
        onCloseModal();
      },
    });
  };

  const onShowPWAPrompt = () => {
    openModal('modal.pwa', '', undefined, onPWAAccept, onPWAReject);
  };

  ////////////////////////////////////////////////////////////
  // PWA
  ////////////////////////////////////////////////////////////

  const [showPWAButton, setShowPWAButton] = React.useState(false);

  const onPWAAvailable = () => {
    Analytics.log('navigation.pwa_offer', 'available');
    setShowPWAButton(true);
  };

  const onPWAProcessed = () => {
    setShowPWAButton(false);
  };

  const onPWAAccept = () => {
    Analytics.log('navigation.pwa_offer', 'accept');
    onCloseModal();
    PwaController.onShowPWAPrompt();
  };

  const onPWAReject = () => {
    Analytics.log('navigation.pwa_offer', 'reject');
    onCloseModal();
  };

  ////////////////////////////////////////////////////////////
  // INIT
  ////////////////////////////////////////////////////////////

  React.useEffect(() => {
    if (window.matchMedia) {
      changeTheme(window.matchMedia('(prefers-color-scheme: dark)').matches ? ApplicationTheme.Dark : ApplicationTheme.Light);
    }
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
      changeTheme(e.matches ? ApplicationTheme.Dark : ApplicationTheme.Light);
    });

    // read persisted values
    UnitController.begin();
    chooseUnit(PersistenceController.retrieve('unitIndex'));
    changeTheme(PersistenceController.retrieve('theme'));
    LanguageController.refreshLanguage();
    Analytics.log('implicit.initialize');

    PwaController.begin(onPWAAvailable, onPWAProcessed);
  }, []);

  // Update Service Worker on launch
  const serviceWorker: any = useServiceWorker();
  const getRegistration = React.useCallback(async () => {
    if ('serviceWorker' in navigator) {
      const promise = Promise.resolve(await navigator.serviceWorker.getRegistration());
      promise
        .then((result) => {
          console.log('Service Worker registration: ', result);
          if (result && result.waiting) {
            serviceWorker.updateAssets(); // Update the service worker automatically if there is a newer version waiting
          }
        })
        .catch((err) => console.log(err));
    }
  }, [serviceWorker]);

  React.useEffect(() => {
    if (serviceWorker) {
      getRegistration();
    }
  }, [getRegistration, serviceWorker]);

  return (
    <>
      <ErrorBoundary FallbackComponent={Fallback}>
        <VaiApplication theme={theme}>
          <PageContainer>
            <Header
              title={pageTitle}
              unitIndex={unit}
              onChooseUnit={chooseUnit}
              unitOptions={UnitController.modeKeys}
              toggleTheme={toggleTheme}
              onMobileMenuOpen={onMobileMenuOpen}
              onHelpOpen={onHelpOpen}
              showPWAButton={showPWAButton}
              onShowPWAPrompt={onShowPWAPrompt}
            />
            <LandingPage
              onHelpOpen={onHelpOpen}
              onOptionsOpen={onOptionsOpen}
              unitPresetIndex={unit}
              unitIndex={unit}
              unitOptions={UnitController.modeKeys}
              onChooseUnit={chooseUnit}
            />
          </PageContainer>
          <MobileMenu
            isOpen={mobileMenuOpen}
            unitIndex={unit}
            onChooseUnit={chooseUnit}
            unitOptions={UnitController.modeKeys}
            themeIndex={theme === ApplicationTheme.Light ? 0 : 1}
            onChooseTheme={chooseTheme}
            onHelpOpen={onHelpOpen}
            onClose={onMobileMenuClose}
            showPWAButton={showPWAButton}
            onShowPWAPrompt={onShowPWAPrompt}
          />
        </VaiApplication>
      </ErrorBoundary>
      <Modals {...modal!} />
    </>
  );
};
